/**
 * Thumbnail click-to-navigate sync for the PDP image gallery.
 *
 * The Vue `Gallery` component (compiled into template.product.js) mounts a
 * native Splide thumbnail nav with `isNavigation: true`, but never actually
 * calls `.sync()` against the main image Splide instance — so clicking a
 * native thumbnail does nothing. There's no source access to fix that
 * directly, so this drives the main gallery's own (working) Next/Prev
 * arrow buttons programmatically instead: it counts how many slides away
 * the clicked thumbnail is and simulates that many arrow clicks, waiting
 * for each transition to actually finish before the next one.
 *
 * Fragility note: this reads specific class names rendered by that Vue
 * bundle (.gallery__main, .slide--main, .is-active, .splide__arrow--next/
 * prev). If that component is ever rebuilt, these could change and the
 * sync would silently stop working (the main image would still swipe
 * fine — only thumbnail click/highlight sync would break).
 *
 * Drives two thumbnail sources with the same logic:
 *  - Desktop: the native (CSS-revealed) .gallery__thumbnails strip
 *  - Mobile: the custom .tde-mobile-thumbnails strip (native one is
 *    hard-destroyed by Splide below 640px, so it doesn't exist there)
 */
(function () {
  // Reads the current slide index from the list's actual transform position,
  // not the "is-active" class. Verified live: after a few rapid arrow clicks,
  // the class can lag behind the real position (still flagging slide 1 as
  // active while slide 2 is the one actually in view) — the transform Splide
  // sets is the reliable ground truth, the class sometimes isn't.
  function getActiveMainIndex(mainList) {
    var list = mainList.querySelector('.splide__list');
    var slides = mainList.querySelectorAll('.slide--main');
    if (!list || !slides.length) return -1;
    var slideWidth = slides[0].getBoundingClientRect().width;
    if (!slideWidth) return -1;
    var tx = 0;
    var match = /matrix\(([^)]+)\)/.exec(getComputedStyle(list).transform);
    if (match) {
      var parts = match[1].split(',');
      tx = parseFloat(parts[4]) || 0;
    }
    var idx = Math.round(-tx / slideWidth);
    if (idx < 0 || idx >= slides.length) return -1;
    return idx;
  }

  // Waits for the active slide to change AND for Splide's CSS transition to
  // actually finish (via transitionend, not just the class flip) before
  // resolving, plus a small settle buffer. Clicking "next" again while the
  // previous transition is still animating is what left the gallery stuck
  // partway between slides — this makes each step wait for a genuine rest
  // state before the next click fires, at the cost of being a bit slower on
  // multi-slide jumps.
  function waitForIndexChange(mainList, fromIndex, timeout) {
    return new Promise(function (resolve) {
      var done = false;
      var list = mainList.querySelector('.splide__list');
      var timer = null;

      function finish() {
        if (done) return;
        done = true;
        observer.disconnect();
        if (list) list.removeEventListener('transitionend', onTransitionEnd);
        clearTimeout(timer);
        // Rapid, JS-triggered transform changes can leave the compositor
        // showing a stale intermediate frame (verified live: the slide
        // visually shows a sliver of the adjacent image) even though the
        // underlying transform/DOM state already settled correctly.
        // Forcing a reflow makes the browser repaint the actual state.
        if (list) {
          var display = list.style.display;
          list.style.display = 'none';
          void list.offsetHeight;
          list.style.display = display;
        }
        setTimeout(function () {
          resolve(getActiveMainIndex(mainList));
        }, 60);
      }

      var observer = new MutationObserver(function () {
        var idx = getActiveMainIndex(mainList);
        if (idx !== -1 && idx !== fromIndex) finish();
      });
      // Watch style (transform) as well as class — transform is now the
      // source of truth for the index, so that's the change that actually
      // matters; class is kept as a secondary trigger for robustness.
      observer.observe(mainList, { attributes: true, subtree: true, attributeFilter: ['class', 'style'] });

      function onTransitionEnd(e) {
        if (e.target === list) finish();
      }
      if (list) list.addEventListener('transitionend', onTransitionEnd);

      timer = setTimeout(finish, timeout || 2500);
    });
  }

  function getThumbs(root) {
    // Both thumbnail strips exist in the DOM at all times (the native one is only
    // hidden via CSS below 641px, not removed), so filter to whichever set is
    // actually visible at the current breakpoint — otherwise indices from the
    // hidden set shift the visible set's mapping to the wrong main slide.
    var all = root.querySelectorAll('.gallery__thumbnails .slide--thumbnail, .tde-mobile-thumbnails__item');
    return Array.prototype.filter.call(all, function (el) {
      return el.offsetParent !== null;
    });
  }

  function highlightThumb(root, index) {
    var thumbs = getThumbs(root);
    thumbs.forEach(function (el, i) {
      el.classList.toggle('is-active', i === index);
    });
  }

  function goToIndex(root, mainList, targetIndex) {
    if (root._tdeNavigating) return;
    root._tdeNavigating = true;

    function step() {
      var current = getActiveMainIndex(mainList);
      if (current === -1 || current === targetIndex) {
        root._tdeNavigating = false;
        return;
      }
      var forward = targetIndex > current;
      var btn = root.querySelector(
        forward ? '.gallery__main .splide__arrow--next' : '.gallery__main .splide__arrow--prev'
      );
      if (!btn || btn.disabled) {
        root._tdeNavigating = false;
        return;
      }
      btn.click();
      waitForIndexChange(mainList, current, 2500).then(function () {
        step();
      });
    }

    step();
  }

  function initRoot(root) {
    var mainList = root.querySelector('.gallery__main');
    if (!mainList) return;

    // Bind every thumbnail once (both strips exist in the DOM regardless of
    // breakpoint), but resolve the target index from the currently-visible
    // set at click time — keeps this correct even if the viewport is resized
    // across the 641px breakpoint after load, without needing to re-init.
    var allThumbs = root.querySelectorAll('.gallery__thumbnails .slide--thumbnail, .tde-mobile-thumbnails__item');
    allThumbs.forEach(function (el) {
      el.addEventListener('click', function () {
        var visible = getThumbs(root);
        var idx = visible.indexOf(el);
        if (idx === -1) return;
        goToIndex(root, mainList, idx);
      });
    });

    var reverseObserver = new MutationObserver(function () {
      var idx = getActiveMainIndex(mainList);
      if (idx !== -1) highlightThumb(root, idx);
    });
    reverseObserver.observe(mainList, { attributes: true, subtree: true, attributeFilter: ['class', 'style'] });
  }

  function init() {
    document.querySelectorAll('.product__gallery-media').forEach(function (root) {
      var attempts = 0;
      var poll = setInterval(function () {
        attempts++;
        var mainList = root.querySelector('.gallery__main');
        if (mainList) {
          clearInterval(poll);
          initRoot(root);
        } else if (attempts > 20) {
          clearInterval(poll);
        }
      }, 100);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
